import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { formatTextForTemplate } from './helpers';
import { CLAUSE_DND_MIME, encodeClauseDnd } from '../../../../docx-native/clauseDnd';
import { docxBase64ToPreviewText, type ClauseBodyFormat } from '../../../../docx-native/clauseBody';
import { ReactComponent as DragIcon } from '../../../../../../../shared/icons/drag.svg';
import { ReactComponent as ChevronDownIcon } from '../../../../../../../shared/icons/chevron-down.svg';
import styles from './styles.module.scss';

interface ClauseTemplateItemProps {
  id: string;
  body: string;
  format: ClauseBodyFormat;
  title: string;
  index: number;
}

const ClauseTemplateItem = ({ id, body, format, title }: ClauseTemplateItemProps) => {
  const [showTemplateText, setShowTemplateText] = useState(false);
  // HTML clauses show their formatted text synchronously. DOCX clauses (body is
  // base64) get their real text extracted from document.xml on mount — a proper
  // preview instead of an opaque label.
  const [docxPreview, setDocxPreview] = useState('');
  useEffect(() => {
    if (format !== 'docx') return;
    let cancelled = false;
    void docxBase64ToPreviewText(body).then((text) => {
      if (!cancelled) setDocxPreview(text);
    });
    return () => {
      cancelled = true;
    };
  }, [format, body]);
  const formattedTemplateText =
    format === 'docx'
      ? formatTextForTemplate(docxPreview || 'Loading preview…')
      : formatTextForTemplate(body);

  // Native HTML5 drag: carry the clause payload (format + body) on a custom MIME
  // so the editor canvas can accept it (and ignore ordinary text drags).
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData(CLAUSE_DND_MIME, encodeClauseDnd({ format, body }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      className={styles.wrapper}
      draggable
      onDragStart={handleDragStart}
      data-id={id}
    >
      <div className={classNames(styles['content'])}>
        <span className={styles.firstRow}>
          <span className={styles['drag-handle']}>
            <DragIcon className={styles['icon-drag']} />
          </span>
          <span className={styles['template-title']}>{title}</span>
          <span onClick={() => setShowTemplateText(!showTemplateText)} className={styles['expand-handle']}>
            <ChevronDownIcon
              className={classNames(styles['icon-expand-arrow'], {
                [styles['icon-expand-arrow-up']]: showTemplateText,
              })}
            />
          </span>
        </span>
        <textarea
          className={classNames(styles.secondRow, {
            [styles['show-template']]: showTemplateText,
          })}
          disabled
          value={formattedTemplateText}
        />
      </div>
    </div>
  );
};

export default ClauseTemplateItem;
